import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, cn, fmtDate } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Chip, Empty, Skeleton } from "../components/ui";
import { PlusCircle, Vote, Calendar, MapPin, ArrowRight, Users2, Archive } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PollWhatsAppShare from "../components/PollWhatsAppShare";

export default function PollsPage() {
  const nav = useNavigate();
  const { club } = useAuth();
  const canManage = ["owner", "admin", "manager"].includes(club?.my_role);
  const [polls, setPolls] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/polls");
      setPolls(data);
    } catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="pb-28">
      <TopBar
        title="Match Polls"
        subtitle="Who's in for the next match"
        right={canManage && (
          <Button size="sm" onClick={() => nav("/polls/new")} data-testid="add-poll-header-btn">
            <PlusCircle size={16}/> New
          </Button>
        )}
      />
      <div className="px-5">
        {polls === null ? (
          <div className="space-y-3">
            {Array.from({length:3}).map((_,i)=><Skeleton key={i} className="h-28"/>)}
          </div>
        ) : polls.length === 0 ? (
          <Card className="p-0">
            <Empty
              icon={Vote}
              title="No polls yet"
              subtitle="Ask your team who's coming, then auto-generate teams."
            />
            {canManage && (
              <div className="p-4 pt-0">
                <Button className="w-full" onClick={() => nav("/polls/new")} data-testid="empty-add-poll-btn">
                  <PlusCircle size={16}/> Create match poll
                </Button>
              </div>
            )}
          </Card>
        ) : (
          <PollList polls={polls} onOpen={(p) => nav(`/polls/${p.id}`)} />
        )}
      </div>
    </div>
  );
}

// Local calendar date as YYYY-MM-DD (a poll for today stays live all day)
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Polls split into Live (today and later, soonest first) and Archive (date passed, newest first). */
export function PollList({ polls, onOpen }) {
  const [view, setView] = useState("live");
  const today = todayIso();
  const day = (p) => (p.date || "").slice(0, 10);
  const live = polls.filter((p) => day(p) >= today).sort((a, b) => day(a).localeCompare(day(b)));
  const archived = polls.filter((p) => day(p) < today).sort((a, b) => day(b).localeCompare(day(a)));
  const shown = view === "live" ? live : archived;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-1 p-1 bg-black/[0.04] rounded-xl text-sm font-semibold" role="tablist">
        {[["live", `Live (${live.length})`], ["archive", `Archive (${archived.length})`]].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={view === key}
            onClick={() => setView(key)}
            className={cn("h-9 rounded-lg", view === key ? "bg-white shadow-sm text-ink" : "text-ink-muted")}
            data-testid={`polls-view-${key}`}
          >
            {label}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <Card className="p-0">
          {view === "live"
            ? <Empty icon={Vote} title="No live polls" subtitle="Polls for upcoming matches show up here." />
            : <Empty icon={Archive} title="Nothing archived yet" subtitle="Polls move here once their match date has passed." />}
        </Card>
      ) : (
        shown.map((p) => <PollCard key={p.id} poll={p} onClick={() => onOpen(p)} archived={view === "archive"} />)
      )}
    </div>
  );
}

export function PollCard({ poll, onClick, archived = false }) {
  const status = archived ? "archived" : poll.status || "published";
  const statusTone = archived || status === "closed" ? "default" : status === "draft" ? "warning" : "success";
  return (
    <Card
      className="py-4 px-5 cursor-pointer"
      onClick={onClick}
      data-testid={`poll-card-${poll.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Chip tone={statusTone}>{status}</Chip>
            <Chip tone="dark" className="capitalize">{poll.match_type}</Chip>
          </div>
          <div className="text-lg font-bold truncate">{poll.title}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-sm text-ink-muted">
            <span className="flex items-center gap-1"><Calendar size={13}/>{fmtDate(poll.date)}{
              poll.start_time && poll.end_time
                ? ` • ${poll.start_time.substring(0, 5)}–${poll.end_time.substring(0, 5)}`
                : poll.time ? ` • ${poll.time}` : ""
            }</span>
            <span className="flex items-center gap-1"><MapPin size={13}/>{poll.ground}</span>
          </div>
          <div className="flex items-center gap-3 mt-3 text-[13px] font-semibold">
            <span className="text-brand flex items-center gap-1"><Users2 size={13}/> IN {poll.counts?.in || 0}</span>
            <span className="text-red-500">OUT {poll.counts?.out || 0}</span>
            <span className="text-amber-600">MAYBE {poll.counts?.maybe || 0}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {!archived && <PollWhatsAppShare poll={poll} variant="ghost" size="icon" data-testid={`poll-share-${poll.id}`} />}
          <ArrowRight size={18} className="text-ink-muted" />
        </div>
      </div>
    </Card>
  );
}
